import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AppTopbar from "../components/AppTopbar";
import VehicleSelector from "../components/VehicleSelector";
import { ROUTES } from "../config/appConfig";
import { companyInfo, vehicleTypes } from "../config/companyConfig";
import { calculateDistance, getTimeEstimate } from "../utils/fareCalculator";
import "../styles/Landing.css";

const LandingVehicleStep = () => {
  const navigate = useNavigate();
  const [routeDraft, setRouteDraft] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return window.sessionStorage.getItem("landingSelectedVehicle");
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedRoute = window.sessionStorage.getItem("landingRouteDraft");

    if (!storedRoute) {
      navigate(ROUTES.home, { replace: true });
      return;
    }

    try {
      setRouteDraft(JSON.parse(storedRoute));
    } catch (error) {
      navigate(ROUTES.home, { replace: true });
    }
  }, [navigate]);

  const routeDistance = useMemo(() => {
    if (!routeDraft?.pickupLocation || !routeDraft?.dropLocation) {
      return null;
    }

    return calculateDistance(
      routeDraft.pickupLocation.lat,
      routeDraft.pickupLocation.lng,
      routeDraft.dropLocation.lat,
      routeDraft.dropLocation.lng
    );
  }, [routeDraft]);

  const timeEstimate = useMemo(() => {
    if (!routeDistance || !selectedVehicle) {
      return null;
    }

    return getTimeEstimate(routeDistance, selectedVehicle);
  }, [routeDistance, selectedVehicle]);

  const handleProceed = () => {
    if (!selectedVehicle || typeof window === "undefined") {
      return;
    }

    window.sessionStorage.setItem("landingSelectedVehicle", selectedVehicle);
    navigate(ROUTES.bookEstimate);
  };

  const handleCustomerLogin = () => {
    [
      "landingBookingFlow",
      "landingRouteDraft",
      "landingSelectedVehicle",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    navigate(ROUTES.login);
  };

  if (!routeDraft) {
    return null;
  }

  return (
    <div className="landing-page">
      <AppTopbar
        brandHref={ROUTES.home}
        title={companyInfo.name}
        navId="landing-step-two-nav"
        pillText="Vehicle Selection"
        actions={(
          <Button variant="outline-light" onClick={handleCustomerLogin}>
            For Customer
          </Button>
        )}
      />

      <section className="estimator-section">
        <Container>
          <div className="section-intro text-center">
            <span className="section-kicker">Choose Vehicle</span>
            <h2 className="section-title">Choose the vehicle on this page</h2>
            <p className="section-copy">
              The route is already saved. Pick the vehicle here, then continue to the fare estimate page.
            </p>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="estimator-form">
                <div className="route-section-shell">
                  <div className="route-section-heading">
                    <span className="route-section-title">Saved route</span>
                    <small>Your route is ready. Choose the vehicle and continue.</small>
                  </div>

                  <div className="summary-grid">
                    <div className="summary-chip">
                      <span>Pickup</span>
                      <strong>{routeDraft.pickupLocation?.name}</strong>
                    </div>
                    <div className="summary-chip">
                      <span>Drop</span>
                      <strong>{routeDraft.dropLocation?.name}</strong>
                    </div>
                  </div>

                  <div className="route-progress-card">
                    <span className="route-progress-pill is-complete">Route saved</span>
                    <span className={`route-progress-pill ${selectedVehicle ? "is-complete" : ""}`}>Vehicle</span>
                    <span className="route-progress-pill">Next: Fare estimate</span>
                  </div>
                </div>

                <div className="vehicle-selector-block is-focused">
                  <div className="route-section-heading">
                    <span className="route-section-title">Vehicle Options</span>
                    <small>Pick one vehicle before moving to the estimate page.</small>
                  </div>

                  <VehicleSelector
                    vehicles={vehicleTypes}
                    selectedVehicle={selectedVehicle}
                    onSelect={setSelectedVehicle}
                  />

                  <Card className="vehicle-match-panel is-active">
                    <Card.Body>
                      <div className="vehicle-match-header">
                        <span className="vehicle-match-kicker">Vehicle status</span>
                        <strong>{selectedVehicle ? vehicleTypes[selectedVehicle]?.name : "Vehicle not chosen yet"}</strong>
                      </div>
                      <p className="vehicle-match-copy">
                        {selectedVehicle
                          ? `Current selection is ${vehicleTypes[selectedVehicle]?.name}. Continue to the next page to review the fare estimate.`
                          : "Choose a vehicle now. The third page will use this selection to show the fare estimate."}
                      </p>
                      <div className="vehicle-match-points">
                        <span>{routeDistance ? `Route: ${routeDistance} km` : "Route ready"}</span>
                        <span>{timeEstimate || "ETA appears after selection"}</span>
                        <span>{selectedVehicle ? `Base fare: Rs ${vehicleTypes[selectedVehicle]?.baseFare}` : "Pick a vehicle"}</span>
                      </div>
                    </Card.Body>
                  </Card>

                  <div className="step-action-row">
                    <Button variant="outline-secondary" className="route-action-btn step-previous-btn" onClick={() => navigate(ROUTES.home)}>
                      Previous
                    </Button>
                    <Button className="btn-book-now step-proceed-btn" size="lg" onClick={handleProceed} disabled={!selectedVehicle}>
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default LandingVehicleStep;
